import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Input,
  Select,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Switch,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiMoreVertical, FiFilter, FiDownload } from "react-icons/fi";
import axios from "axios";

// Interfaces
interface Area {
  id: number;
  descripcion: string;
}

interface Prioridad {
  id: number;
  descripcion: string;
  color: string;
}

interface EstadoExpediente {
  id: number;
  descripcion: string;
  color: string;
}

interface Oficio {
  id: string;
  numeroFolio: string;
  fechaHoraIngreso: string;
  fechaOficio: string;
  idPrioridad: number;
  prioridad: Prioridad;
  remitente: string;
  personaDependenciaEnvia: string;
  tieneAnexos: boolean;
  descripcionAnexos: string | null;
  idAreaRemite: number;
  areaRemite: Area;
  areaRemiteOtra: string | null;
  tieneFechaTermino: boolean;
  fechaTermino: string | null;
  correoElectronico: string | null;
  telefono: string | null;
  direccion: string | null;
  idAreaDestino: number;
  areaDestino: Area;
  areaDestinoOtra: string | null;
  comentarios: string | null;
  observaciones: string | null;
  idEstadoExpediente: number;
  estadoExpediente: EstadoExpediente;
  usuarioCreacion: string | null;
  fechaCreacion: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";

const OficialiaIndex: React.FC = () => {
  // Estados
  const [oficios, setOficios] = useState<Oficio[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  // Catálogos
  const [areas, setAreas] = useState<Area[]>([]);
  const [prioridades, setPrioridades] = useState<Prioridad[]>([]);
  const [estados, setEstados] = useState<EstadoExpediente[]>([]);

  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [filtroAreaRemite, setFiltroAreaRemite] = useState("");
  const [filtroAreaDestino, setFiltroAreaDestino] = useState("");
  const [filtroPrioridad, setFiltroPrioridad] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  // Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenView, onOpen: onOpenView, onClose: onCloseView } = useDisclosure();
  const [selectedOficio, setSelectedOficio] = useState<Oficio | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Formulario
  const [formData, setFormData] = useState({
    fechaOficio: "",
    idPrioridad: 1,
    remitente: "",
    personaDependenciaEnvia: "",
    tieneAnexos: false,
    descripcionAnexos: "",
    idAreaRemite: 1,
    areaRemiteOtra: "",
    tieneFechaTermino: false,
    fechaTermino: "",
    correoElectronico: "",
    telefono: "",
    direccion: "",
    idAreaDestino: 1,
    areaDestinoOtra: "",
    comentarios: "",
    observaciones: "",
    idEstadoExpediente: 1,
  });

  const toast = useToast();

  // Cargar catálogos
  const loadCatalogos = useCallback(async () => {
    try {
      const [areasRes, prioridadesRes, estadosRes] = await Promise.all([
        axios.get(`${API_URL}/api/oficios/catalogos/areas`),
        axios.get(`${API_URL}/api/oficios/catalogos/prioridades`),
        axios.get(`${API_URL}/api/oficios/catalogos/estados`),
      ]);
      setAreas(areasRes.data);
      setPrioridades(prioridadesRes.data);
      setEstados(estadosRes.data);
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los catálogos",
        status: "error",
        duration: 3000,
      });
    }
  }, [toast]);

  // Cargar oficios
  const loadOficios = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", pagination.page.toString());
      params.append("limit", pagination.limit.toString());
      if (busqueda) params.append("busqueda", busqueda);
      if (fechaInicio) params.append("fechaInicio", fechaInicio);
      if (fechaFin) params.append("fechaFin", fechaFin);
      if (filtroAreaRemite) params.append("idAreaRemite", filtroAreaRemite);
      if (filtroAreaDestino) params.append("idAreaDestino", filtroAreaDestino);
      if (filtroPrioridad) params.append("idPrioridad", filtroPrioridad);
      if (filtroEstado) params.append("idEstadoExpediente", filtroEstado);

      const response = await axios.get(`${API_URL}/api/oficios?${params.toString()}`);
      setOficios(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error al cargar oficios:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los oficios",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, busqueda, fechaInicio, fechaFin, filtroAreaRemite, filtroAreaDestino, filtroPrioridad, filtroEstado, toast]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    loadOficios();
  }, [loadOficios]);

  // Handlers
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadOficios();
  };

  const handleClearFilters = () => {
    setBusqueda("");
    setFechaInicio("");
    setFechaFin("");
    setFiltroAreaRemite("");
    setFiltroAreaDestino("");
    setFiltroPrioridad("");
    setFiltroEstado("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setFormData({
      fechaOficio: new Date().toISOString().split("T")[0],
      idPrioridad: 1,
      remitente: "",
      personaDependenciaEnvia: "",
      tieneAnexos: false,
      descripcionAnexos: "",
      idAreaRemite: 1,
      areaRemiteOtra: "",
      tieneFechaTermino: false,
      fechaTermino: "",
      correoElectronico: "",
      telefono: "",
      direccion: "",
      idAreaDestino: 1,
      areaDestinoOtra: "",
      comentarios: "",
      observaciones: "",
      idEstadoExpediente: 1,
    });
    onOpen();
  };

  const handleOpenEdit = (oficio: Oficio) => {
    setIsEditing(true);
    setSelectedOficio(oficio);
    setFormData({
      fechaOficio: oficio.fechaOficio.split("T")[0],
      idPrioridad: oficio.idPrioridad,
      remitente: oficio.remitente,
      personaDependenciaEnvia: oficio.personaDependenciaEnvia,
      tieneAnexos: oficio.tieneAnexos,
      descripcionAnexos: oficio.descripcionAnexos || "",
      idAreaRemite: oficio.idAreaRemite,
      areaRemiteOtra: oficio.areaRemiteOtra || "",
      tieneFechaTermino: oficio.tieneFechaTermino,
      fechaTermino: oficio.fechaTermino ? oficio.fechaTermino.split("T")[0] : "",
      correoElectronico: oficio.correoElectronico || "",
      telefono: oficio.telefono || "",
      direccion: oficio.direccion || "",
      idAreaDestino: oficio.idAreaDestino,
      areaDestinoOtra: oficio.areaDestinoOtra || "",
      comentarios: oficio.comentarios || "",
      observaciones: oficio.observaciones || "",
      idEstadoExpediente: oficio.idEstadoExpediente,
    });
    onOpen();
  };

  const handleOpenView = (oficio: Oficio) => {
    setSelectedOficio(oficio);
    onOpenView();
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && selectedOficio) {
        await axios.put(`${API_URL}/api/oficios/${selectedOficio.id}`, formData);
        toast({
          title: "Oficio actualizado",
          status: "success",
          duration: 3000,
        });
      } else {
        await axios.post(`${API_URL}/api/oficios`, formData);
        toast({
          title: "Oficio registrado",
          description: "El oficio se ha registrado correctamente",
          status: "success",
          duration: 3000,
        });
      }
      onClose();
      loadOficios();
    } catch (error) {
      console.error("Error al guardar oficio:", error);
      toast({
        title: "Error",
        description: "No se pudo guardar el oficio",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Está seguro de eliminar este oficio?")) {
      try {
        await axios.delete(`${API_URL}/api/oficios/${id}`);
        toast({
          title: "Oficio eliminado",
          status: "success",
          duration: 3000,
        });
        loadOficios();
      } catch (error) {
        console.error("Error al eliminar oficio:", error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el oficio",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAreaName = (area: Area, otraArea: string | null) => {
    if (area.descripcion === "Otra" && otraArea) {
      return otraArea;
    }
    return area.descripcion;
  };

  return (
    <Box p={4} pt={79} width="100%">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold" color="#5b182b">
          Oficialía - Control de Oficios
        </Text>
        <Button
          leftIcon={<FiPlus />}
          bg="#5b182b"
          color="white"
          _hover={{ bg: "#691f30" }}
          onClick={handleOpenCreate}
        >
          Nuevo Oficio
        </Button>
      </Flex>

      {/* Filtros */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={4}>
        <VStack spacing={4} align="stretch">
          <HStack spacing={4} flexWrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por folio, remitente..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </InputGroup>

            <Input
              type="date"
              placeholder="Fecha inicio"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              maxW="180px"
            />
            <Input
              type="date"
              placeholder="Fecha fin"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              maxW="180px"
            />
          </HStack>

          <HStack spacing={4} flexWrap="wrap">
            <Select
              placeholder="Área remite"
              value={filtroAreaRemite}
              onChange={(e) => setFiltroAreaRemite(e.target.value)}
              maxW="200px"
            >
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.descripcion}
                </option>
              ))}
            </Select>

            <Select
              placeholder="Área destino"
              value={filtroAreaDestino}
              onChange={(e) => setFiltroAreaDestino(e.target.value)}
              maxW="200px"
            >
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.descripcion}
                </option>
              ))}
            </Select>

            <Select
              placeholder="Prioridad"
              value={filtroPrioridad}
              onChange={(e) => setFiltroPrioridad(e.target.value)}
              maxW="150px"
            >
              {prioridades.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.descripcion}
                </option>
              ))}
            </Select>

            <Select
              placeholder="Estado"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              maxW="150px"
            >
              {estados.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.descripcion}
                </option>
              ))}
            </Select>

            <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSearch}>
              <FiFilter /> Filtrar
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Limpiar
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* Tabla de oficios */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="xl" color="#5b182b" />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead bg="#5b182b">
                <Tr>
                  <Th color="white">Folio</Th>
                  <Th color="white">Fecha Ingreso</Th>
                  <Th color="white">Fecha Oficio</Th>
                  <Th color="white">Remitente</Th>
                  <Th color="white">Área Remite</Th>
                  <Th color="white">Área Destino</Th>
                  <Th color="white">Prioridad</Th>
                  <Th color="white">Estado</Th>
                  <Th color="white">Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {oficios.length > 0 ? (
                  oficios.map((oficio) => (
                    <Tr key={oficio.id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="bold">{oficio.numeroFolio}</Td>
                      <Td>{formatDateTime(oficio.fechaHoraIngreso)}</Td>
                      <Td>{formatDate(oficio.fechaOficio)}</Td>
                      <Td>
                        <Text noOfLines={1} maxW="150px">
                          {oficio.remitente}
                        </Text>
                      </Td>
                      <Td>
                        <Text noOfLines={1} maxW="120px">
                          {getAreaName(oficio.areaRemite, oficio.areaRemiteOtra)}
                        </Text>
                      </Td>
                      <Td>
                        <Text noOfLines={1} maxW="120px">
                          {getAreaName(oficio.areaDestino, oficio.areaDestinoOtra)}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          bg={oficio.prioridad.color}
                          color="white"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {oficio.prioridad.descripcion}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          bg={oficio.estadoExpediente.color}
                          color="white"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {oficio.estadoExpediente.descripcion}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList zIndex={9999}>
                            <MenuItem icon={<FiEye />} onClick={() => handleOpenView(oficio)}>
                              Ver detalle
                            </MenuItem>
                            <MenuItem icon={<FiEdit2 />} onClick={() => handleOpenEdit(oficio)}>
                              Editar
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              color="red.500"
                              onClick={() => handleDelete(oficio.id)}
                            >
                              Eliminar
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={8}>
                      <Text color="gray.500">No se encontraron oficios</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            {/* Paginación */}
            <Flex justify="space-between" align="center" mt={4}>
              <Text fontSize="sm" color="gray.600">
                Mostrando {oficios.length} de {pagination.total} registros
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  isDisabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <Text fontSize="sm">
                  Página {pagination.page} de {pagination.totalPages}
                </Text>
                <Button
                  size="sm"
                  isDisabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </HStack>
            </Flex>
          </>
        )}
      </Box>

      {/* Modal de Crear/Editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
            {isEditing ? "Editar Oficio" : "Registrar Nuevo Oficio"}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              {/* Fecha del oficio */}
              <FormControl isRequired>
                <FormLabel>Fecha del Oficio</FormLabel>
                <Input
                  type="date"
                  value={formData.fechaOficio}
                  onChange={(e) => setFormData({ ...formData, fechaOficio: e.target.value })}
                />
              </FormControl>

              {/* Prioridad */}
              <FormControl isRequired>
                <FormLabel>Prioridad</FormLabel>
                <Select
                  value={formData.idPrioridad}
                  onChange={(e) => setFormData({ ...formData, idPrioridad: parseInt(e.target.value) })}
                >
                  {prioridades.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Remitente */}
              <FormControl isRequired>
                <FormLabel>Remitente</FormLabel>
                <Input
                  placeholder="Nombre del remitente"
                  value={formData.remitente}
                  onChange={(e) => setFormData({ ...formData, remitente: e.target.value })}
                />
              </FormControl>

              {/* Persona/Dependencia que envía */}
              <FormControl isRequired>
                <FormLabel>Persona/Dependencia que Envía</FormLabel>
                <Input
                  placeholder="Nombre de la persona o dependencia"
                  value={formData.personaDependenciaEnvia}
                  onChange={(e) => setFormData({ ...formData, personaDependenciaEnvia: e.target.value })}
                />
              </FormControl>

              {/* Anexos */}
              <HStack align="center">
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">¿Tiene anexos?</FormLabel>
                  <Switch
                    isChecked={formData.tieneAnexos}
                    onChange={(e) => setFormData({ ...formData, tieneAnexos: e.target.checked })}
                  />
                </FormControl>
              </HStack>

              {formData.tieneAnexos && (
                <FormControl>
                  <FormLabel>Descripción de Anexos</FormLabel>
                  <Textarea
                    placeholder="Describa los anexos"
                    value={formData.descripcionAnexos}
                    onChange={(e) => setFormData({ ...formData, descripcionAnexos: e.target.value })}
                  />
                </FormControl>
              )}

              {/* Área que remite */}
              <FormControl isRequired>
                <FormLabel>Área que Remite</FormLabel>
                <Select
                  value={formData.idAreaRemite}
                  onChange={(e) => setFormData({ ...formData, idAreaRemite: parseInt(e.target.value) })}
                >
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {areas.find((a) => a.id === formData.idAreaRemite)?.descripcion === "Otra" && (
                <FormControl>
                  <FormLabel>Especifique el Área</FormLabel>
                  <Input
                    placeholder="Nombre del área"
                    value={formData.areaRemiteOtra}
                    onChange={(e) => setFormData({ ...formData, areaRemiteOtra: e.target.value })}
                  />
                </FormControl>
              )}

              {/* Fecha término */}
              <HStack align="center">
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">¿Tiene fecha de término?</FormLabel>
                  <Switch
                    isChecked={formData.tieneFechaTermino}
                    onChange={(e) => setFormData({ ...formData, tieneFechaTermino: e.target.checked })}
                  />
                </FormControl>
              </HStack>

              {formData.tieneFechaTermino && (
                <FormControl>
                  <FormLabel>Fecha de Término</FormLabel>
                  <Input
                    type="date"
                    value={formData.fechaTermino}
                    onChange={(e) => setFormData({ ...formData, fechaTermino: e.target.value })}
                  />
                </FormControl>
              )}

              {/* Contacto */}
              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Correo Electrónico</FormLabel>
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={formData.correoElectronico}
                    onChange={(e) => setFormData({ ...formData, correoElectronico: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Teléfono</FormLabel>
                  <Input
                    placeholder="Número de teléfono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Dirección</FormLabel>
                <Input
                  placeholder="Dirección de contacto"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                />
              </FormControl>

              {/* Área destino */}
              <FormControl isRequired>
                <FormLabel>Área Destino</FormLabel>
                <Select
                  value={formData.idAreaDestino}
                  onChange={(e) => setFormData({ ...formData, idAreaDestino: parseInt(e.target.value) })}
                >
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {areas.find((a) => a.id === formData.idAreaDestino)?.descripcion === "Otra" && (
                <FormControl>
                  <FormLabel>Especifique el Área Destino</FormLabel>
                  <Input
                    placeholder="Nombre del área"
                    value={formData.areaDestinoOtra}
                    onChange={(e) => setFormData({ ...formData, areaDestinoOtra: e.target.value })}
                  />
                </FormControl>
              )}

              {/* Comentarios y observaciones */}
              <FormControl>
                <FormLabel>Comentarios</FormLabel>
                <Textarea
                  placeholder="Comentarios adicionales"
                  value={formData.comentarios}
                  onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Observaciones</FormLabel>
                <Textarea
                  placeholder="Observaciones"
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                />
              </FormControl>

              {/* Estado del expediente */}
              <FormControl>
                <FormLabel>Estado del Expediente</FormLabel>
                <Select
                  value={formData.idEstadoExpediente}
                  onChange={(e) => setFormData({ ...formData, idEstadoExpediente: parseInt(e.target.value) })}
                >
                  {estados.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Botones */}
              <HStack justify="flex-end" spacing={4} pt={4}>
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button bg="#5b182b" color="white" _hover={{ bg: "#691f30" }} onClick={handleSubmit}>
                  {isEditing ? "Actualizar" : "Registrar"}
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal de Ver Detalle */}
      <Modal isOpen={isOpenView} onClose={onCloseView} size="xl">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
            Detalle del Oficio
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6}>
            {selectedOficio && (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold">Número de Folio:</Text>
                  <Text>{selectedOficio.numeroFolio}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Fecha/Hora de Ingreso:</Text>
                  <Text>{formatDateTime(selectedOficio.fechaHoraIngreso)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Fecha del Oficio:</Text>
                  <Text>{formatDate(selectedOficio.fechaOficio)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Prioridad:</Text>
                  <Badge bg={selectedOficio.prioridad.color} color="white" px={2} py={1}>
                    {selectedOficio.prioridad.descripcion}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Remitente:</Text>
                  <Text>{selectedOficio.remitente}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Persona/Dependencia:</Text>
                  <Text>{selectedOficio.personaDependenciaEnvia}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Tiene Anexos:</Text>
                  <Text>{selectedOficio.tieneAnexos ? "Sí" : "No"}</Text>
                </HStack>
                {selectedOficio.tieneAnexos && selectedOficio.descripcionAnexos && (
                  <Box>
                    <Text fontWeight="bold">Descripción de Anexos:</Text>
                    <Text>{selectedOficio.descripcionAnexos}</Text>
                  </Box>
                )}
                <HStack justify="space-between">
                  <Text fontWeight="bold">Área que Remite:</Text>
                  <Text>{getAreaName(selectedOficio.areaRemite, selectedOficio.areaRemiteOtra)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Área Destino:</Text>
                  <Text>{getAreaName(selectedOficio.areaDestino, selectedOficio.areaDestinoOtra)}</Text>
                </HStack>
                {selectedOficio.tieneFechaTermino && selectedOficio.fechaTermino && (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">Fecha de Término:</Text>
                    <Text>{formatDate(selectedOficio.fechaTermino)}</Text>
                  </HStack>
                )}
                {selectedOficio.correoElectronico && (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">Correo:</Text>
                    <Text>{selectedOficio.correoElectronico}</Text>
                  </HStack>
                )}
                {selectedOficio.telefono && (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">Teléfono:</Text>
                    <Text>{selectedOficio.telefono}</Text>
                  </HStack>
                )}
                {selectedOficio.direccion && (
                  <Box>
                    <Text fontWeight="bold">Dirección:</Text>
                    <Text>{selectedOficio.direccion}</Text>
                  </Box>
                )}
                {selectedOficio.comentarios && (
                  <Box>
                    <Text fontWeight="bold">Comentarios:</Text>
                    <Text>{selectedOficio.comentarios}</Text>
                  </Box>
                )}
                {selectedOficio.observaciones && (
                  <Box>
                    <Text fontWeight="bold">Observaciones:</Text>
                    <Text>{selectedOficio.observaciones}</Text>
                  </Box>
                )}
                <HStack justify="space-between">
                  <Text fontWeight="bold">Estado:</Text>
                  <Badge bg={selectedOficio.estadoExpediente.color} color="white" px={2} py={1}>
                    {selectedOficio.estadoExpediente.descripcion}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Usuario Creación:</Text>
                  <Text>{selectedOficio.usuarioCreacion || "N/A"}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Fecha Creación:</Text>
                  <Text>{formatDateTime(selectedOficio.fechaCreacion)}</Text>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OficialiaIndex;
